// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t616 · B-2476 · LAS NOTAS DE SESIÓN SALEN CON QUIEN LAS ESCRIBIÓ
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  Las notas de sesión confidenciales no van en la exportación de la clínica: las lee solo su autor. Esta es su única salida: cada
//  profesional descarga las SUYAS, de todos sus pacientes, con su contraseña, en un ZIP cifrado con una contraseña que se enseña una
//  vez. Va en la tarjeta de las notas de la ficha y en la pantalla de una clínica dada de baja o en pausa (components/AvisoImpago),
//  que es donde hace falta cuando la clínica se va. A quien no ha escrito ninguna no se le enseña nada. El servidor manda
//  (backend/services/notasSesion · exportarMias): esconder aquí no es la seguridad.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../services/api'
import { msgError } from '../../utils/errores'
import { t, tx } from '../../utils/i18n'

const K = 'components.notasSesion.DescargarMisNotas.'
const nota = { fontSize: '12.5px', lineHeight: 1.5, color: 'var(--text-secondary)' }
const inp = { width: '100%', maxWidth: '320px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' }

// El fichero, al navegador: se monta de lo que contesta el servidor y no se queda en ningún sitio más.
function bajar(nombre, base64) {
  const bin = atob(base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/zip' }))
  const a = document.createElement('a')
  a.href = url
  a.download = nombre
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

export default function DescargarMisNotas() {
  const { data } = useQuery({ queryKey: ['notas-sesion-mias'], queryFn: () => api.get('/notas-sesion/mias').then((r) => r.data).catch(() => null) })
  const [abierto, setAbierto] = useState(false)
  const [password, setPassword] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const [hecho, setHecho] = useState(null)
  if (!data || !data.disponible || !data.cuantas) return null
  const descargar = async () => {
    if (!password) return setError(t(K + 'falta_contrasena', 'Escribe tu contraseña de Biango.'))
    setEnviando(true); setError('')
    try {
      const r = (await api.post('/notas-sesion/mias/exportar', { password })).data
      bajar(r.nombre, r.zip)
      setHecho({ contrasena: r.contrasena, cuantas: r.cuantas, ilegibles: r.ilegibles }); setPassword('')
    } catch (e) { setError(msgError(e, t(K + 'no_se_ha_podido', 'No se han podido descargar. Vuelve a intentarlo.'))) }
    setEnviando(false)
  }
  return (
    <div data-descargar-mis-notas="" style={{ marginTop: '12px' }}>
      {!abierto && (
        <button type="button" className="btn btn-secondary" onClick={() => setAbierto(true)}>
          {tx(K + 'boton', 'Descargar mis notas de sesión ({{n}})', { n: data.cuantas })}
        </button>
      )}
      {abierto && !hecho && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={nota}>{t(K + 'explica', 'Son las notas que has escrito tú, de todos tus pacientes. Llevan datos de salud: guárdalas en un sitio seguro. En Biango no cambia nada. Se abren con 7-Zip, Keka o WinRAR; el Explorador de Windows y el Finder del Mac no abren este cifrado.')}</div>
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={inp}
            aria-label={t(K + 'tu_contrasena', 'Tu contraseña de Biango')} placeholder={t(K + 'tu_contrasena', 'Tu contraseña de Biango')} />
          {error && <div role="alert" style={{ ...nota, color: 'var(--danger)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" disabled={enviando} onClick={descargar}>{enviando ? t(K + 'preparando', 'Preparándolas…') : t(K + 'descargar', 'Descargar')}</button>
            <button type="button" className="btn btn-secondary" disabled={enviando} onClick={() => { setAbierto(false); setError(''); setPassword('') }}>{t(K + 'cancelar', 'Cancelar')}</button>
          </div>
        </div>
      )}
      {hecho && (
        <div role="status" data-mis-notas-contrasena="" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ ...nota, fontWeight: 700, color: 'var(--text-primary)' }}>{t(K + 'apunta', 'Apunta esta contraseña: solo se enseña ahora y sin ella el fichero no se abre.')}</div>
          <code style={{ fontSize: '15px', userSelect: 'all', wordBreak: 'break-all' }}>{hecho.contrasena}</code>
          {hecho.ilegibles > 0 && <div style={{ ...nota, color: 'var(--danger)' }}>{tx(K + 'ilegibles', '{{n}} notas no se han podido leer y van marcadas: avisa a soporte.', { n: hecho.ilegibles })}</div>}
        </div>
      )}
    </div>
  )
}
